import React, { useEffect, useMemo, useState } from 'react';
import { genAttributes } from '@onward-delivery/core';
import { css } from '@emotion/react';
import { Button, Grid } from '@material-ui/core';
import { colors } from '@/styles';
import { useSwappedAttributes } from '@/components/ShipmentForm/hooks';
import AddressAutocomplete from '@/components/ShipmentForm/ModifiedAddressAutocomplete';
import GoogleMap, { Marker } from '@/components/GoogleMap';
import {
    ErrorText,
    OnwardToggle,
    RadioLabelInfo,
    TabRow,
    TabSection,
    ToggleBlurb,
} from '@/components/ShipmentForm/blocks';
import { useClientUser, useOrderNotes } from '@/hooks';
import diagnoseAddress from '@/utilities/diagnoseAddress';
import {
    autocompleteFullAddress,
    autocompleteLatLngPatch,
    isResolvedAutocompleteAddress,
    toFullState,
    toShortState,
} from '@/utilities/address';
import { OnwardTextField } from '../../InputFields';
import {
    autocompleteAddressPatch,
    clearGeocodingLeg,
    formatGeocodingSuggestion,
    geocodingSuggestionCoords,
    hasReviewGeocodingSuggestion,
    isApproximateGeocodingSuggestion,
} from '../geocoding';

const Address = ({ isDirty, order, hasError, callbacks, options }) => {
    const { client_id, user_id } = useClientUser();
    const [viewMapOverride, setViewMap] = useState(false);

    const { is_custom, geocode_failed, address, city, state, street, zip, lat, long, unit } =
        useSwappedAttributes(order);
    const {
        address: pu_address,
        street: pu_street,
        city: pu_city,
        state: pu_state,
        zip: pu_zip,
    } = useSwappedAttributes(order, true);

    const isImport = window.location.pathname === '/import' || window.location.pathname.includes('job');
    const userType = (() => {
        if (!isImport && order?.carrier_id && client_id !== order?.shipper_id) {
            return 'Carrier';
        } else {
            return 'Shipper';
        }
    })();
    const orderNotes = useOrderNotes([order], userType);
    const currentNote = orderNotes?.delivery?.[0] || {};

    const modifyInitialNote = (e) => {
        if (currentNote?.note_id) {
            const currentNotes = [...order.notes];
            const currentNoteIdx = currentNotes.findIndex((n) => n.note_id === currentNote.note_id);
            currentNotes[currentNoteIdx] = {
                ...currentNote,
                note: e.target.value,
            };
            callbacks.modifyOrder({ notes: currentNotes });
        } else {
            callbacks.addNote({
                note: e.target.value,
                source_user_type: userType,
                source_user_id: user_id,
                is_acknowledgement: false,
                requires_acknowledgement: false,
                acknowledged_by_shipper: false,
                acknowledged_by_admin: false,
                private_to: null,
                type: 'Delivery',
            });
        }
    };

    const coords = useMemo(() => {
        if (order && lat && long) {
            return order[lat] && order[long] ? { lat: order[lat], lng: order[long] } : null;
        }

        return null;
    }, [order, lat, long]);

    const viewMap = useMemo(() => {
        return order[is_custom] || viewMapOverride;
    }, [viewMapOverride, order, is_custom]);

    const allowCustom = useMemo(() => {
        return !!order.order_id;
    }, [order]);

    const geocodingLeg = useMemo(() => {
        const leg = genAttributes(order).geocoding;
        if (!leg) throw new Error('genAttributes() must provide geocoding leg metadata; update @onward-delivery/core.');
        return leg;
    }, [order]);
    const geocodingSuggestion = order?.geocoding?.[geocodingLeg]?.suggestion;
    const hasGeocodingSuggestion = hasReviewGeocodingSuggestion(order?.geocoding, geocodingLeg);
    const suggestionLabel = formatGeocodingSuggestion(geocodingSuggestion);
    const suggestionCoords = geocodingSuggestionCoords(geocodingSuggestion);
    const isApproximateSuggestion = isApproximateGeocodingSuggestion(geocodingSuggestion);
    const suggestionPrefix = isApproximateSuggestion ? 'Approximate suggested map location' : 'Suggested map location';
    const suggestionAction = isApproximateSuggestion ? 'Use approximate pin' : 'Use suggested pin';
    const mapCenter = coords || suggestionCoords;
    const clearCurrentGeocoding = () => clearGeocodingLeg(order.geocoding, geocodingLeg);
    const autocompletePatch = (value, split) =>
        autocompleteAddressPatch({
            attrs: { geocode_failed, address, street, city, state, zip, lat, long },
            geocoding: order.geocoding,
            leg: geocodingLeg,
            value,
            split,
        });
    const applySuggestedPin = () => {
        callbacks.modifyOrder({
            [lat]: geocodingSuggestion.lat,
            [long]: geocodingSuggestion.lng,
            [is_custom]: true,
            [geocode_failed]: false,
            auto_assign_failed: false,
            geocoding: clearCurrentGeocoding(),
        });
        callbacks.makeDirty([lat, long, is_custom, geocode_failed]);
    };
    const manualPinPatch = (nextIsCustom = true) => ({
        [is_custom]: nextIsCustom,
        [geocode_failed]: false,
        ...(nextIsCustom && hasGeocodingSuggestion ? { [lat]: null, [long]: null } : {}),
        ...(!nextIsCustom || !hasGeocodingSuggestion ? { geocoding: clearCurrentGeocoding() } : {}),
    });

    const hasAddressFieldError = useMemo(
        () =>
            [address, street, city, state, zip, lat, long].some(
                (attr) => hasError[attr] && (options.startDirty || isDirty[attr])
            ) || order[geocode_failed],
        [address, street, city, state, zip, lat, long, hasError, options.startDirty, isDirty, order, geocode_failed]
    );

    // Diagnose saved unresolved addresses
    const [diagnosis, setDiagnosis] = useState(null);
    const addressForDiagnosis = order[address];
    const needsDiagnosis =
        !!order.order_id &&
        hasAddressFieldError &&
        !order[is_custom] &&
        !!addressForDiagnosis &&
        !hasGeocodingSuggestion;

    useEffect(() => {
        if (!needsDiagnosis) {
            setDiagnosis(null);
            return undefined;
        }
        let cancelled = false;
        diagnoseAddress(addressForDiagnosis)
            .then((result) => {
                if (cancelled) return;
                setDiagnosis(result);
            })
            .catch(() => {
                if (cancelled) return;
                setDiagnosis(null);
            });
        return () => {
            cancelled = true;
        };
    }, [needsDiagnosis, addressForDiagnosis]);

    let addressError = null;
    if (hasAddressFieldError || hasGeocodingSuggestion) {
        let message = hasAddressFieldError ? 'Address is invalid' : 'Address needs review';
        if (diagnosis && !diagnosis.ok) {
            // Prefer diagnostics
            message = diagnosis.message;
        }
        addressError = (
            <Grid
                css={css`
                    color: ${colors.reds[1]};
                    margin-bottom: 4px;
                `}
                container
                direction="column"
            >
                <ErrorText>{message}</ErrorText>
                {hasGeocodingSuggestion ? (
                    <Grid
                        item
                        css={css`
                            margin-top: 4px;
                        `}
                    >
                        {suggestionPrefix}: {suggestionLabel || 'approximate location'}
                        <Button
                            color="primary"
                            disabled={options.disableAddressEditing}
                            onClick={applySuggestedPin}
                            size="small"
                        >
                            {suggestionAction}
                        </Button>
                    </Grid>
                ) : null}
            </Grid>
        );
    }

    return (
        <>
            <TabSection>Address</TabSection>
            {allowCustom ? addressError : null}
            <TabRow>
                <Grid item xs={9}>
                    {order[is_custom] ? (
                        <OnwardTextField
                            variant="outlined"
                            color="primary"
                            label="Address"
                            value={order[street]}
                            error={hasError[street] && (options.startDirty || isDirty[street])}
                            disabled={options.disableAddressEditing}
                            onBlur={() => callbacks.makeDirty([street, ...(order.is_middle_mile ? [pu_street] : [])])}
                            onChange={(e) => {
                                callbacks.modifyOrder({
                                    [street]: e.target.value,
                                    [geocode_failed]: false,
                                    auto_assign_failed: false,
                                    geocoding: clearCurrentGeocoding(),
                                });
                                callbacks.makeDirty([
                                    address,
                                    street,
                                    ...(order.is_middle_mile ? [pu_address, pu_street] : []),
                                ]);
                            }}
                            fullWidth
                        />
                    ) : (
                        <AddressAutocomplete
                            disabled={options.disableAddressEditing}
                            state={{
                                street: order[street],
                                city: order[city],
                                state: order[state],
                                zip: order[zip],
                            }}
                            handleAddressUpdate={async (value, split) => {
                                if (options.disableGeocoding) {
                                    // Strong coords when geocoding is off
                                    callbacks.modifyOrder({
                                        ...autocompletePatch(value, split),
                                        ...autocompleteLatLngPatch(split, lat, long),
                                    });
                                } else if (!isResolvedAutocompleteAddress(split)) {
                                    callbacks.modifyOrder(autocompletePatch(value, split));
                                } else {
                                    // Seed enrich() with placeId result
                                    const results = await callbacks.enrichOrder(
                                        address,
                                        autocompleteFullAddress(value, split),
                                        {
                                            geocodeOverride: split,
                                        }
                                    );
                                    if (results.errors.geocodeFailed) {
                                        callbacks.onError('Geocode failed. Please try again.');
                                    }
                                    callbacks.modifyOrder({ geocoding: clearCurrentGeocoding() });
                                }

                                callbacks.makeDirty([
                                    address,
                                    street,
                                    city,
                                    state,
                                    zip,
                                    ...(order.is_middle_mile ? [pu_address, pu_street, pu_city, pu_state, pu_zip] : []),
                                ]);
                            }}
                            error={hasAddressFieldError}
                        />
                    )}
                </Grid>
                <Grid item xs={3}>
                    <OnwardTextField
                        variant="outlined"
                        color="primary"
                        label="Unit/Suite #"
                        value={order[unit]}
                        onChange={(e) =>
                            callbacks.modifyOrder({ [unit]: e.target.value, geocoding: clearCurrentGeocoding() })
                        }
                        fullWidth
                    />
                </Grid>
            </TabRow>
            <TabRow>
                <Grid item xs={4}>
                    <OnwardTextField
                        fullWidth
                        label="City"
                        variant="outlined"
                        color="primary"
                        value={order[city]}
                        error={hasError[city] && (options.startDirty || isDirty[city])}
                        onBlur={() => callbacks.makeDirty([city])}
                        onChange={(e) =>
                            callbacks.modifyOrder({
                                [city]: e.target.value,
                                geocoding: clearCurrentGeocoding(),
                            })
                        }
                        InputLabelProps={{
                            shrink: !!order[city],
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <OnwardTextField
                        fullWidth
                        variant="outlined"
                        color="primary"
                        label="State"
                        value={toFullState(order[state])}
                        error={hasError[state] && (options.startDirty || isDirty[state])}
                        onBlur={() => callbacks.makeDirty([state])}
                        onChange={(e) =>
                            callbacks.modifyOrder({
                                [state]: toShortState(e.target.value),
                                geocoding: clearCurrentGeocoding(),
                            })
                        }
                        InputLabelProps={{
                            shrink: !!order[state],
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <OnwardTextField
                        fullWidth
                        variant="outlined"
                        color="primary"
                        label="Zip"
                        value={order[zip]}
                        error={hasError[zip] && (options.startDirty || isDirty[zip])}
                        onBlur={() => callbacks.makeDirty([zip])}
                        onChange={(e) =>
                            callbacks.modifyOrder({
                                [zip]: e.target.value,
                                geocoding: clearCurrentGeocoding(),
                            })
                        }
                        InputLabelProps={{
                            shrink: !!order[zip],
                        }}
                    />
                </Grid>
            </TabRow>
            <TabRow>
                <Grid item xs={12}>
                    <OnwardTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        label="Comments"
                        multiline={true}
                        value={currentNote?.note || ''}
                        onChange={modifyInitialNote}
                    />
                </Grid>
            </TabRow>

            {allowCustom ? (
                <TabRow>
                    <Grid item>
                        <OnwardToggle
                            css={css`
                                margin: 0;
                            `}
                            disabled={options.disableAddressEditing}
                            value={order[is_custom] || false}
                            onChange={(e) => {
                                const nextIsCustom = e.target.checked;
                                callbacks.modifyOrder(manualPinPatch(nextIsCustom));
                                callbacks.makeDirty([
                                    address,
                                    street,
                                    ...(nextIsCustom && hasGeocodingSuggestion ? [lat, long] : []),
                                    ...(order.is_middle_mile ? [pu_address, pu_street] : []),
                                ]);
                            }}
                        />
                    </Grid>
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            margin-left: 12px;
                            justify-content: center;
                        `}
                    >
                        <Grid container item>
                            <RadioLabelInfo
                                css={css`
                                    color: #000;
                                    font-weight: 400;
                                `}
                            >
                                Having trouble with your address?&nbsp;
                                <a
                                    css={css`
                                        cursor: pointer;
                                    `}
                                    onClick={() => {
                                        if (options.disableAddressEditing) return;
                                        callbacks.modifyOrder(manualPinPatch());
                                        callbacks.makeDirty([
                                            address,
                                            street,
                                            ...(hasGeocodingSuggestion ? [lat, long] : []),
                                            ...(order.is_middle_mile ? [pu_address, pu_street] : []),
                                        ]);
                                    }}
                                >
                                    Place a pin on the map
                                </a>
                            </RadioLabelInfo>
                        </Grid>

                        {order[is_custom] ? (
                            <Grid
                                container
                                item
                                css={css`
                                    margin-top: 8px;
                                `}
                            >
                                <ToggleBlurb />
                            </Grid>
                        ) : null}
                    </Grid>
                </TabRow>
            ) : null}

            {viewMap && (
                <Grid
                    item
                    css={css`
                        min-height: 350px;
                        margin-bottom: 12px;
                        border-radius: 6px;
                        border: ${hasError[lat] || hasError[long] ? `1px solid #f44336;` : '1px solid transparent;'};
                    `}
                >
                    <GoogleMap
                        zoom={15}
                        onClick={(e) => {
                            if (options.disableGeocoding) {
                                const x = e.latLng.lat();
                                const y = e.latLng.lng();

                                callbacks.modifyOrder({
                                    [lat]: x,
                                    [long]: y,
                                    geocoding: clearCurrentGeocoding(),
                                });
                            } else {
                                callbacks.geocodeLatLong(e);
                                callbacks.modifyOrder({ geocoding: clearCurrentGeocoding() });
                            }

                            callbacks.makeDirty([lat, long]);
                        }}
                        draggableCursor="pointer"
                        gestureHandling="greedy"
                        center={mapCenter || undefined}
                    >
                        {coords ? <Marker position={coords} /> : null}
                        {!coords && suggestionCoords ? (
                            <Marker position={suggestionCoords} opacity={0.7} label={{ text: '?', color: 'white' }} />
                        ) : null}
                    </GoogleMap>
                </Grid>
            )}
        </>
    );
};

export default Address;
